import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Frases de relleno mientras el agente trabaja.
 *
 * REGLA: ninguna puede afirmar una acción concreta y comprobable (#911).
 *
 * El catálogo anterior decía cosas como «Consultando fuentes...» o «Verificando
 * datos...», que describen acciones específicas. Cuando el agente hacía otra cosa
 * —ejecutar código, generar un documento— el indicador informaba en falso, que es
 * el mismo defecto que #508 por el otro extremo: contarle al usuario algo que no
 * ocurrió. Y como un indicador que miente se ve igual que uno sano, tapó durante
 * meses la intermitencia real que mide #909.
 *
 * Estas son estados internos y guiños de bandada: comunican «sigo aquí» sin
 * afirmar nada que pueda ser falso. Mismo registro que los verbos de Claude
 * (Reflocking, Percolating, Ruminating), que nadie lee como una afirmación.
 */
const PHRASES = [
  { i18nKey: 'status.thinking', fallback: 'Pensando...' },
  { i18nKey: 'status.pondering', fallback: 'Dándole vueltas...' },
  { i18nKey: 'status.ruminating', fallback: 'Rumiando...' },
  { i18nKey: 'status.musing', fallback: 'Cavilando...' },
  { i18nKey: 'status.plotting', fallback: 'Tramando...' },
  { i18nKey: 'status.cooking', fallback: 'Cocinando...' },
  { i18nKey: 'status.spinning', fallback: 'Hilando fino...' },
  { i18nKey: 'status.flapping', fallback: 'Aleteando...' },
  { i18nKey: 'status.fluttering', fallback: 'Revoloteando...' },
  { i18nKey: 'status.soaring', fallback: 'Tomando impulso...' },
  { i18nKey: 'status.onit', fallback: 'En ello...' },
  { i18nKey: 'status.focused', fallback: 'Concentrado...' },
];

// Fisher-Yates shuffle (returns new array)
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * useRotatingStatus - frase a mostrar mientras el agente está activo.
 *
 * Con `currentTask`: se muestra la tarea real, **fija**. No se alterna con relleno.
 *   Antes de #911 se sustituía por una frase del catálogo en cada tick, o sea que
 *   teniendo información verdadera se tapaba la mitad del tiempo con decoración.
 * Sin `currentTask`: rota el catálogo, que es lo que da la sensación de actividad.
 *
 * @param {Object|null} agentStatus - { agentName, currentTask, lastUpdated }
 * @param {(key: string, fallback: string) => string} [t] - traductor de react-i18next.
 *   Sin él se devuelve el texto español verbatim, igual que `getToolLabel`.
 * @returns {string|null} displayTask - frase a mostrar, o null si no está activo
 */
export default function useRotatingStatus(agentStatus, t) {
  const [displayTask, setDisplayTask] = useState(null);
  const phraseIndexRef = useRef(0);
  const shuffledRef = useRef(shuffle(PHRASES));
  const intervalRef = useRef(null);
  const prevTaskRef = useRef(null);

  const isActive = Boolean(agentStatus?.agentName);
  const currentTask = agentStatus?.currentTask || '';

  // `t` se guarda en ref: react-i18next devuelve una función nueva en cada render,
  // y meterla en las deps reiniciaría el intervalo constantemente.
  const tRef = useRef(t);
  tRef.current = t;

  const getNextPhrase = useCallback(() => {
    if (phraseIndexRef.current >= shuffledRef.current.length) {
      shuffledRef.current = shuffle(PHRASES);
      phraseIndexRef.current = 0;
    }
    const entry = shuffledRef.current[phraseIndexRef.current++];
    return tRef.current ? tRef.current(entry.i18nKey, entry.fallback) : entry.fallback;
  }, []);

  const tick = useCallback(() => {
    // Con tarea real no se rota: la información verdadera no se sustituye por
    // relleno. El tick solo anima la espera cuando no sabemos qué está pasando.
    if (currentTask) return;
    setDisplayTask(getNextPhrase());
  }, [currentTask, getNextPhrase]);

  // When currentTask changes from N8N, show it immediately
  useEffect(() => {
    if (!isActive) return;
    if (currentTask && currentTask !== prevTaskRef.current) {
      setDisplayTask(currentTask);
    }
    prevTaskRef.current = currentTask;
  }, [currentTask, isActive]);

  // Main interval
  useEffect(() => {
    if (!isActive) {
      setDisplayTask(null);
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
      phraseIndexRef.current = 0;
      return;
    }

    // Initial phrase
    if (currentTask) {
      setDisplayTask(currentTask);
    } else {
      setDisplayTask(getNextPhrase());
    }

    // Schedule ticks with random interval 3-5s
    const scheduleTick = () => {
      const delay = 3000 + Math.random() * 2000;
      intervalRef.current = setTimeout(() => {
        tick();
        scheduleTick();
      }, delay);
    };
    scheduleTick();

    return () => {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    };
  }, [isActive, currentTask, tick, getNextPhrase]);

  return displayTask;
}

// Exportado para los tests: la garantía de #911 es sobre el catálogo, no sobre el hook.
export { PHRASES };
